import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { ArrowRight, User } from "@phosphor-icons/react";

const SplitBillModal = ({ open, onClose, order, items, onSplit }) => {
  const [guestCount, setGuestCount] = useState(2);
  const [itemAssignments, setItemAssignments] = useState({});
  const [guestTotals, setGuestTotals] = useState({});

  useEffect(() => {
    if (open && items.length > 0) {
      const initial = {};
      items.forEach(item => { initial[item.id] = 1; });
      setItemAssignments(initial);
    }
  }, [open, items]);

  useEffect(() => {
    const totals = {};
    for (let i = 1; i <= guestCount; i++) { totals[i] = 0; }
    items.forEach(item => {
      const guest = itemAssignments[item.id] || 1;
      if (totals[guest] !== undefined) { totals[guest] += item.total_price; }
    });
    Object.keys(totals).forEach(guest => {
      const subtotal = totals[guest];
      const tax = subtotal * 0.08;
      totals[guest] = { subtotal, tax, total: subtotal + tax };
    });
    setGuestTotals(totals);
  }, [itemAssignments, items, guestCount]);

  const handleAssignItem = (itemId, guestNum) => { setItemAssignments(prev => ({ ...prev, [itemId]: guestNum })); };
  const handleSplit = () => {
    const splits = Object.entries(itemAssignments).map(([itemId, guestNumber]) => ({ item_id: itemId, guest_number: guestNumber }));
    onSplit(splits);
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh]" data-testid="split-bill-modal">
        <DialogHeader><DialogTitle className="font-heading text-xl">Split Bill</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4 mb-4">
          <Label>Number of Guests</Label>
          <div className="flex items-center gap-2">
            {[2, 3, 4, 5, 6].map(num => (
              <Button key={num} variant={guestCount === num ? "default" : "outline"} size="sm" onClick={() => setGuestCount(num)} className="w-10 h-10" data-testid={`guest-count-${num}`}>{num}</Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-2">Order Items</p>
            <ScrollArea className="h-[300px] border border-border rounded-lg p-2">
              {items.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50 mb-1" data-testid={`split-item-${item.id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.menu_item_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">${item.total_price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <ArrowRight size={14} className="text-muted-foreground" />
                    <div className="flex gap-1">
                      {Array.from({ length: guestCount }, (_, i) => i + 1).map(num => (
                        <Button key={num} variant={itemAssignments[item.id] === num ? "default" : "outline"} size="sm" onClick={() => handleAssignItem(item.id, num)} className="w-8 h-8 p-0" data-testid={`assign-${item.id}-guest-${num}`}>{num}</Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Guest Totals</p>
            <div className="space-y-2">
              {Array.from({ length: guestCount }, (_, i) => i + 1).map(guestNum => (
                <div key={guestNum} className="split-guest-card p-4 rounded-lg bg-secondary/30" data-testid={`guest-${guestNum}-total`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center"><User size={16} weight="bold" /></div>
                    <span className="font-medium">Guest {guestNum}</span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {items.filter(item => itemAssignments[item.id] === guestNum).map(item => (
                      <div key={item.id} className="flex justify-between text-sm"><span className="text-muted-foreground">{item.menu_item_name}</span><span className="font-mono">${item.total_price.toFixed(2)}</span></div>
                    ))}
                    {items.filter(item => itemAssignments[item.id] === guestNum).length === 0 && <p className="text-sm text-muted-foreground italic">No items assigned</p>}
                  </div>
                  <div className="border-t border-border pt-2 space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${(guestTotals[guestNum]?.subtotal || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax</span><span className="font-mono">${(guestTotals[guestNum]?.tax || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-medium"><span>Total</span><span className="font-mono">${(guestTotals[guestNum]?.total || 0).toFixed(2)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSplit} data-testid="confirm-split-btn">Apply Split</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SplitBillModal;
